// BCM-234 / BCM-897 — Product ↔ Plant ↔ Storage Location mapping helpers.
//
// The FE model for a product's storage locations moved OFF the deprecated
// product-level `receivingStorageLocationIDsFromSponsorERP` string and ONTO the
// per-product `productPlantSLocMapping: [{ plantID, storageLocationID }]` list.
//   • /fetchProductDetails returns it (sibling of `productDetails`).
//   • /updateProductDetails consumes it (with a per-row `markForDelete`), gated by
//     the `updateProductPlantSLocMapping: true` flag.
// A product has ONE receiving plant (receivingPlantIDFromSponsorERP), so every
// mapping row is that plant paired with one of the product's storage locations.

const asStr = (v) => String(v == null ? "" : v).trim();

// BCM-897: plant and storage-location IDs are a mix of pure numbers ("0002",
// "9998"), pure text ("VETT") and combinations ("SL10"), so a plain string sort
// reads wrong — it puts "10" before "2" and "SL10" before "SL2". numeric:true
// compares runs of digits as numbers, which orders all three shapes the way a
// person reads them; sensitivity:"base" keeps "b2" beside "B2" instead of
// sorting every lowercase value into its own block after the uppercase ones.
export const compareIds = (a, b) =>
  asStr(a).localeCompare(asStr(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });

// Ascending COPY — the caller's array is never sorted in place, so this is safe
// on props and on state held elsewhere.
export const sortIds = (values) =>
  [...(Array.isArray(values) ? values : [])].sort(compareIds);

// Normalize a storage-location value (array OR comma-separated string) to a clean,
// de-duplicated array of trimmed strings.
export const toSlocArray = (v) => {
  const seen = new Set();
  const out = [];
  const push = (s) => {
    const t = asStr(s);
    if (t && !seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  };
  if (Array.isArray(v)) v.forEach(push);
  else if (typeof v === "string") v.split(",").forEach(push);
  return out;
};

// Current storage locations for a product, read from a fetchProductDetails
// `productPlantSLocMapping` list. Single plant per product, so we don't filter by
// plant — every row belongs to this product's plant.
export const slocsFromMapping = (mapping) =>
  toSlocArray((Array.isArray(mapping) ? mapping : []).map((m) => m?.storageLocationID));

// Build the productPlantSLocMapping payload for /updateProductDetails:
//   • every current storage location for the plant  → markForDelete: false
//   • any storage location that was in the ORIGINAL mapping but is no longer
//     present                                        → markForDelete: true
// Sending both kept and removed rows is robust whether Corda merges or replaces
// the set: removals are always explicit.
export const buildProductPlantSLocMapping = (
  plantID,
  currentSlocs,
  originalMapping
) => {
  const plant = asStr(plantID);
  const current = toSlocArray(currentSlocs);
  const currentSet = new Set(current);

  const rows = current.map((storageLocationID) => ({
    plantID: plant,
    storageLocationID,
    markForDelete: false,
  }));

  (Array.isArray(originalMapping) ? originalMapping : []).forEach((m) => {
    const s = asStr(m?.storageLocationID);
    if (s && !currentSet.has(s)) {
      rows.push({
        plantID: asStr(m?.plantID) || plant,
        storageLocationID: s,
        markForDelete: true,
      });
    }
  });

  return rows;
};
